import React, { useState, useEffect } from 'react';
import { BookOpen, Lock, ShoppingCart, Eye, LogIn, User, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SecurePDFViewer from './SecurePDFViewer';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = window.location.origin;

// Official Stripe "S" logo
const StripeLogo = () => (
  <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor" aria-hidden="true">
    <path d="M13.6 10.3c0-1.1.9-1.5 2.3-1.5 2.1 0 4.7.6 6.8 1.7V4.3C20.4 3.4 18.1 3 15.9 3c-5.5 0-9.2 2.9-9.2 7.8 0 7.6 10.4 6.4 10.4 9.6 0 1.3-1.1 1.7-2.6 1.7-2.3 0-5.3-.9-7.7-2.2v6.3C9.5 27.2 12.1 27.7 15 27.7c5.7 0 9.6-2.8 9.6-7.8 0-8.2-10.4-6.8-10.4-9.6z"/>
  </svg>
);

const BookStoreSection = () => {
  const { t } = useLanguage();
  const { user, login, isAuthenticated } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [purchasedBookIds, setPurchasedBookIds] = useState([]);
  const [viewingBook, setViewingBook] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null); // runtime PayPal config from backend

  useEffect(() => {
    fetchBooks();
    // Fetch PayPal config at runtime (so .env changes don't require frontend rebuild)
    fetch(`${BACKEND_URL}/api/paypal/config`)
      .then(r => r.ok ? r.json() : null)
      .then(cfg => { if (cfg && cfg.configured && cfg.client_id) setPaypalConfig(cfg); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPurchaseStatus();
    }
  }, [isAuthenticated]);

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/books/`);
      if (response.ok) {
        const data = await response.json();
        setBooks(data.books || []);
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPurchaseStatus = async () => {
    try {
      const purchasedResponse = await fetch(`${BACKEND_URL}/api/books/user/purchased-books`, {
        credentials: 'include'
      });
      if (purchasedResponse.ok) {
        const data = await purchasedResponse.json();
        setPurchasedBookIds(data.book_ids || []);
      }
    } catch (error) {
      console.error('Failed to fetch purchase status:', error);
    }
  };

  const handleReadBook = async (book) => {
    // VISITORS CANNOT ACCESS - Must be registered
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    // Check access for logged-in users
    try {
      const response = await fetch(`${BACKEND_URL}/api/books/${book.book_id}/check-access`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.has_access) {
        // Open secure PDF viewer (no download possible)
        setViewingBook(book);
      } else {
        setSelectedBook(book);
      }
    } catch (error) {
      console.error('Access check failed:', error);
    }
  };

  const handlePurchase = async (bookId) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      setSelectedBook(null);
      return;
    }

    if (purchasedBookIds.includes(bookId)) {
      const book = books.find(b => b.book_id === bookId);
      if (book) setViewingBook(book);
      setSelectedBook(null);
      return;
    }

    const book = books.find(b => b.book_id === bookId);
    if (!book) return;
    setSelectedBook(null);

    if (book.price && book.price > 0) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            book_id: bookId,
            origin_url: window.location.origin
          })
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } catch (err) {
        console.error('Stripe checkout failed:', err);
      }
      return;
    }

    // Free book
    try {
      const res = await fetch(`${BACKEND_URL}/api/books/${bookId}/request-purchase`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.status === 'completed') {
        setPurchasedBookIds(prev => [...prev, bookId]);
      }
    } catch (err) {
      console.error('Purchase request failed:', err);
    }
  };

  const handleLoginClick = () => {
    setShowLoginPrompt(false);
    login();
  };

  // Build a stable, derived options object for PayPalScriptProvider to avoid
  // re-initializing the SDK on every render (known cause of buttons not rendering).
  const paypalOptions = React.useMemo(() => paypalConfig ? {
    clientId: paypalConfig.client_id,
    currency: paypalConfig.currency || 'USD',
    intent: 'capture',
    components: 'buttons',
  } : null, [paypalConfig]);

  const sectionContent = (
    <section id="books" className="py-16" style={{
      background: 'linear-gradient(180deg, #251845 0%, #1f1340 100%)'
    }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('bookStoreTitle')}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('bookStoreDesc')}
          </p>
          
          {/* Registration Notice for Visitors */}
          {!isAuthenticated && books.length > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 bg-pink-600/20 border border-pink-500/30 text-pink-300 px-4 py-2 rounded-full text-sm">
              <LogIn className="w-4 h-4" />
              Sign in with Google to purchase or read books
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center">
            <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : books.length === 0 ? (
          <div className="max-w-md mx-auto text-center">
            <div className="bg-[#1a1a2e]/70 rounded-xl p-8 border border-gray-700/50">
              <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Coming Soon</h3>
              <p className="text-gray-400">Books will be available here shortly.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {books.map((book) => (
              <div
                key={book.book_id}
                className="bg-[#1a1a2e]/70 rounded-xl overflow-hidden border border-gray-700/50 transition-all duration-300 hover:border-pink-500/30 hover:shadow-lg hover:shadow-pink-500/10 group"
              >
                <div className="aspect-[3/4] bg-gray-800 relative overflow-hidden">
                  {book.cover_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_url}`}
                      alt={book.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
                      <BookOpen className="w-16 h-16 text-gray-500" />
                    </div>
                  )}
                  {/* Price Badge */}
                  <div className="absolute top-3 right-3">
                    {book.is_free ? (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                        Free
                      </span>
                    ) : (
                      <span className="bg-pink-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                        ${book.price}
                      </span>
                    )}
                  </div>
                  
                  {/* Login Required Overlay for Visitors */}
                  {!isAuthenticated && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-center text-white">
                        <Lock className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Login Required</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-white font-semibold mb-1 line-clamp-1">{book.title}</h3>
                  {book.subtitle && (
                    <p className="text-gray-400 text-sm mb-1 line-clamp-1">{book.subtitle}</p>
                  )}
                  <p className="text-pink-400 text-sm mb-3">{book.author}</p>
                  
                  {/* Book Available After Purchase */}
                  {isAuthenticated && purchasedBookIds.includes(book.book_id) && !book.is_free && (
                    <div className="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium" dir="rtl">
                          الكتاب متاح لك الآن
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => handleReadBook(book)}
                    className={`w-full text-sm ${
                      purchasedBookIds.includes(book.book_id)
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                        : 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700'
                    } text-white`}
                  >
                    {!isAuthenticated ? (
                      <><LogIn className="w-4 h-4 mr-2" /> Sign In to Access</>
                    ) : purchasedBookIds.includes(book.book_id) ? (
                      <><Eye className="w-4 h-4 mr-2" /> اقرأ الآن</>
                    ) : book.is_free ? (
                      <><Eye className="w-4 h-4 mr-2" /> Read Now</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" /> Get Access</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Login Required Modal - For Visitors */}
      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowLoginPrompt(false)} />
          <div className="relative bg-[#1a1a2e] rounded-2xl p-8 max-w-md w-full border border-gray-700 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <User className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Registration Required</h3>
            <p className="text-gray-400 mb-6">
              You need to sign in with your Google account to purchase or read books. 
              It's free and takes just a few seconds!
            </p>
            <div className="space-y-3">
              <Button
                onClick={handleLoginClick}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white py-6 text-lg"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In with Google
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowLoginPrompt(false)}
                className="w-full text-gray-400 hover:text-white"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal - For Logged In Users */}
      {selectedBook && isAuthenticated && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedBook(null)} />
          <div className="relative bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-2">{selectedBook.title}</h3>
            {selectedBook.description && (
              <p className="text-gray-400 text-sm mb-4 line-clamp-3">{selectedBook.description}</p>
            )}
            <div className="flex items-center justify-between mb-5 pb-5 border-b border-gray-700">
              <span className="text-gray-300">Price:</span>
              <span className="text-2xl font-bold text-pink-400">${selectedBook.price}</span>
            </div>

            {selectedBook.price && selectedBook.price > 0 ? (
              <>
                <p className="text-sm text-gray-400 mb-3 text-center">
                  Choose your payment method
                </p>
                <div className="grid grid-cols-2 gap-3 mb-4" dir="ltr">
                  {/* PayPal — LEFT — official branded button (only if configured) */}
                  <div data-testid="buy-paypal-btn" className="w-full min-w-[140px]">
                    {paypalConfig ? (
                      <PayPalButtons
                        key={selectedBook.book_id}
                        fundingSource="paypal"
                        style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 48, tagline: false }}
                        createOrder={async () => {
                          const res = await fetch(`${BACKEND_URL}/api/paypal/create-order`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ book_id: selectedBook.book_id })
                          });
                          if (!res.ok) {
                            const err = await res.json().catch(() => ({ detail: 'PayPal unavailable' }));
                            throw new Error(err.detail || 'Create order failed');
                          }
                          const data = await res.json();
                          return data.order_id;
                        }}
                        onApprove={async (data) => {
                          const res = await fetch(`${BACKEND_URL}/api/paypal/capture-order/${data.orderID}`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (!res.ok) {
                            const err = await res.json().catch(() => ({ detail: 'Capture failed' }));
                            alert(err.detail || 'Payment capture failed');
                            return;
                          }
                          const result = await res.json();
                          if (result.status === 'completed') {
                            setPurchasedBookIds(prev => Array.from(new Set([...prev, result.book_id])));
                            setSelectedBook(null);
                            alert('Purchase successful! You can now read the book.');
                          }
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          alert('PayPal payment failed. Please try Stripe or try again.');
                        }}
                      />
                    ) : (
                      <div className="h-12 rounded-md border border-gray-700 flex items-center justify-center text-xs text-gray-500">PayPal not configured</div>
                    )}
                  </div>

                  {/* Stripe — RIGHT — official Stripe purple + logo + "Stripe" wordmark */}
                  <Button
                    data-testid="buy-stripe-btn"
                    onClick={() => handlePurchase(selectedBook.book_id)}
                    className="h-12 bg-[#635bff] hover:bg-[#4e47cc] text-white font-semibold flex items-center justify-center gap-2 shadow-md"
                    aria-label="Pay with Stripe"
                  >
                    <StripeLogo />
                    <span className="text-base tracking-wide">Stripe</span>
                  </Button>
                </div>

                <Button
                  data-testid="purchase-cancel-btn"
                  variant="outline"
                  onClick={() => setSelectedBook(null)}
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSelectedBook(null)}
                  className="flex-1 border-gray-600 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="get-free-book-btn"
                  onClick={() => handlePurchase(selectedBook.book_id)}
                  className="flex-1 bg-pink-600 hover:bg-pink-700 text-white"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Get It
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Secure PDF Viewer - NO DOWNLOAD POSSIBLE */}
      {viewingBook && (
        <SecurePDFViewer
          bookId={viewingBook.book_id}
          bookTitle={viewingBook.title}
          onClose={() => setViewingBook(null)}
        />
      )}

    </section>
  );

  // Wrap the entire section in PayPalScriptProvider when PayPal is configured.
  // SDK loads ONCE for the whole section (not per modal mount) — critical for
  // PayPalButtons to render reliably.
  return paypalOptions ? (
    <PayPalScriptProvider options={paypalOptions}>{sectionContent}</PayPalScriptProvider>
  ) : sectionContent;
};

export default BookStoreSection;
